/**
 * Sticky presentation state for the player's most recently used input device.
 *
 * InputSystem is the owner of keyboard, pointer, and gamepad activity, so it
 * publishes the modality here at the same point it accepts each source. UI
 * surfaces subscribe to this tiny projection to choose truthful shortcut
 * hints; the store does not route, consume, or authorize gameplay actions.
 */

import type { ControllerPresentationProjection } from './gamepadMappingProfile';

export type InputModality = 'keyboard-pointer' | 'gamepad' | 'touch';

export interface InputModalitySnapshot {
  modality: InputModality;
  gamepadIndex: number | null;
  gamepadId: string | null;
  controller: ControllerPresentationProjection | null;
}

const KEYBOARD_POINTER_SNAPSHOT: InputModalitySnapshot = Object.freeze({
  modality: 'keyboard-pointer',
  gamepadIndex: null,
  gamepadId: null,
  controller: null,
});

const TOUCH_SNAPSHOT: InputModalitySnapshot = Object.freeze({
  modality: 'touch',
  gamepadIndex: null,
  gamepadId: null,
  controller: null,
});

let snapshot = KEYBOARD_POINTER_SNAPSHOT;
const listeners = new Set<() => void>();

function publish(next: InputModalitySnapshot): void {
  if (
    snapshot.modality === next.modality
    && snapshot.gamepadIndex === next.gamepadIndex
    && snapshot.gamepadId === next.gamepadId
    && snapshot.controller === next.controller
  ) {
    return;
  }

  snapshot = next;
  listeners.forEach(listener => listener());
}

export const InputModalityStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  getSnapshot(): InputModalitySnapshot {
    return snapshot;
  },

  noteKeyboardPointerActivity(): void {
    publish(Object.freeze({
      ...KEYBOARD_POINTER_SNAPSHOT,
      controller: snapshot.controller,
    }));
  },

  noteControllerConnected(controller: ControllerPresentationProjection): void {
    publish(Object.freeze({
      ...snapshot,
      gamepadIndex: snapshot.modality === 'gamepad'
        ? controller.controllerIndex
        : null,
      gamepadId: snapshot.modality === 'gamepad'
        ? controller.controllerId
        : null,
      controller,
    }));
  },

  noteGamepadActivity(controller: ControllerPresentationProjection): void {
    publish(Object.freeze({
      modality: 'gamepad',
      gamepadIndex: controller.controllerIndex,
      gamepadId: controller.controllerId,
      controller,
    }));
  },

  noteTouchActivity(): void {
    publish(Object.freeze({
      ...TOUCH_SNAPSHOT,
      controller: snapshot.controller,
    }));
  },

  noteNoGamepadConnected(): void {
    if (snapshot.modality === 'gamepad' || snapshot.controller !== null) {
      publish(KEYBOARD_POINTER_SNAPSHOT);
    }
  },
};

/** Test-only reset; production state is replaced by accepted input activity. */
export function resetInputModalityForTests(): void {
  snapshot = KEYBOARD_POINTER_SNAPSHOT;
  listeners.clear();
}
